import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Gift, Copy, Check, Trophy, Users, Sparkles, Pencil, X } from 'lucide-react';

export default function ReferralPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draftCode, setDraftCode] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/referrals/my-code')
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const copyCode = () => {
    navigator.clipboard.writeText(data?.referral_code || '');
    setCopied(true);
    toast.success('Referral code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareUrl = `${window.location.origin}/register?ref=${data?.referral_code || ''}`;
  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Referral link copied!');
  };

  const startEditing = () => {
    setDraftCode((data?.referral_code || '').toUpperCase());
    setEditing(true);
  };
  const cancelEditing = () => {
    setEditing(false);
    setDraftCode('');
  };
  const saveCode = async () => {
    const next = (draftCode || '').trim().toUpperCase();
    if (!/^[A-Z0-9]{4,12}$/.test(next)) {
      toast.error('Use 4-12 letters or numbers (A-Z, 0-9).');
      return;
    }
    if (next === (data?.referral_code || '').toUpperCase()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const { data: resp } = await api.put('/referrals/my-code', { referral_code: next });
      setData(prev => ({ ...prev, referral_code: resp.referral_code }));
      setEditing(false);
      toast.success('Referral code updated!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update code — try another.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-4 border-[#56dfb4] border-t-transparent rounded-full" />
      </div>
    );
  }

  const isInstructor = !!data.is_instructor || !!user?.is_instructor;
  const progressPct = Math.min(100, (data.progress_to_milestone / 10) * 100);

  return (
    <div data-testid="referral-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-center mb-10">
        <div className="w-16 h-16 rounded-2xl bg-[#56dfb4]/10 flex items-center justify-center mx-auto mb-4">
          <Gift className="w-8 h-8 text-[#56dfb4]" />
        </div>
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Refer a Friend
        </h1>
        <p className="text-slate-500 mt-2 max-w-xl mx-auto">
          Every Meet For Mahjong member has a personal referral code to share with friends,
          students, players, instructors, and anyone who loves Mahjong.
        </p>
      </div>

      {/* Why Referrals Matter */}
      <Card className="border-slate-100 mb-6" data-testid="why-referrals-card">
        <CardContent className="p-6">
          <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Why Referrals Matter</h3>
          <p className="text-sm text-slate-600">
            Meet For Mahjong works best when more players, instructors, and event hosts are part of it.
            Every referral helps more people find games, discover instructors, connect locally, and keep
            the Mahjong community growing.
          </p>
        </CardContent>
      </Card>

      {/* Your Code */}
      <Card className="border-[#56dfb4]/30 bg-[#56dfb4]/5 mb-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Referral Code</h3>
            {!editing && (
              <button
                onClick={startEditing}
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#b456df] focus:outline-none focus:underline"
                data-testid="edit-referral-code-btn"
              >
                <Pencil className="w-3 h-3" />
                Change
              </button>
            )}
          </div>
          {editing ? (
            <div data-testid="edit-referral-code-form">
              <div className="flex gap-2 items-center">
                <Input
                  value={draftCode}
                  onChange={e => setDraftCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12))}
                  maxLength={12}
                  placeholder="LISA2026"
                  className="text-center text-lg font-mono font-bold tracking-widest rounded-xl bg-white"
                  data-testid="edit-referral-code-input"
                  autoFocus
                />
                <Button
                  onClick={saveCode}
                  disabled={saving}
                  className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] gap-1 px-4"
                  data-testid="save-referral-code-btn"
                >
                  <Check className="w-4 h-4" />
                  {saving ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  onClick={cancelEditing}
                  variant="outline"
                  className="rounded-full px-3"
                  data-testid="cancel-referral-code-btn"
                  disabled={saving}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                4-12 characters, letters and numbers only. Old links using your previous code will stop working once you save.
              </p>
            </div>
          ) : (
            <div className="flex gap-2 items-center">
              <Input
                value={data.referral_code}
                readOnly
                className="text-center text-lg font-mono font-bold tracking-widest rounded-xl bg-white"
                data-testid="referral-code-display"
              />
              <Button
                onClick={copyCode}
                className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] gap-1 px-4"
                data-testid="copy-code-btn"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          )}
          <div className="mt-3">
            <p className="text-xs text-slate-500 mb-1">Or share your referral link:</p>
            <div className="flex gap-2 items-center">
              <Input value={shareUrl} readOnly className="text-xs rounded-xl bg-white" data-testid="referral-link-display" />
              <Button onClick={copyLink} variant="outline" size="sm" className="rounded-full px-3">
                <Copy className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Two-path explainer — player vs instructor */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Card className="border-slate-100" data-testid="player-referrals-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-[#56c6df]/15 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#56c6df]" />
              </div>
              <h3 className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Player Referrals</h3>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              Share your referral code with other players. When someone uses your code to upgrade to a
              <strong> Verified Player Membership</strong>, they get one free month, and you get one
              free month added to your membership too. There's no limit to how many free months you
              can earn.
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2">
                <span className="text-[#56dfb4] mt-0.5 flex-shrink-0">✓</span>
                <span>Your friend gets <strong>one free month</strong></span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#56dfb4] mt-0.5 flex-shrink-0">✓</span>
                <span>You get <strong>one free month</strong> added to your Verified Player Membership</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#56dfb4] mt-0.5 flex-shrink-0">✓</span>
                <span>No limit — the more players you invite, the more free time you earn</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card className="border-slate-100" data-testid="instructor-referrals-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-[#b456df]/15 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#b456df]" />
              </div>
              <h3 className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Instructor Referrals</h3>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              Instructors can share their referral code with players. When a player uses your code
              to upgrade to <strong>Verified Player</strong>, they get one free month, and you earn
              credit toward your instructor membership. Refer 10 Verified Players and get one free
              month of <strong>Instructor Unlimited</strong>.
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2">
                <span className="text-[#b456df] mt-0.5 flex-shrink-0">✓</span>
                <span>The player gets <strong>one free month</strong></span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#b456df] mt-0.5 flex-shrink-0">✓</span>
                <span>You earn <strong>credit toward your instructor membership</strong></span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#b456df] mt-0.5 flex-shrink-0">✓</span>
                <span>Refer <strong>10 Verified Players</strong> and get <strong>one free month of Instructor Unlimited</strong></span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Instructor milestone tracker (only instructors) */}
      {isInstructor && (
        <Card
          className="border-2 border-[#b456df]/30 bg-gradient-to-br from-[#b456df]/10 via-white to-[#56dfb4]/5 mb-6"
          data-testid="instructor-progress-card"
        >
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-2xl bg-[#b456df]/15 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-[#b456df]" />
              </div>
              <div>
                <h3 className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Your Instructor Milestone Progress
                </h3>
                {data.unlimited_reward_active && (
                  <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-[#56dfb4] text-slate-900 text-[10px] font-bold uppercase tracking-wider" data-testid="referral-unlimited-active-badge">
                    Bonus Instructor Unlimited month active
                  </span>
                )}
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              <strong className="text-[#b456df]" data-testid="referral-instructor-count">{data.instructor_referral_count}</strong>{' '}
              Verified Player {data.instructor_referral_count === 1 ? 'referral' : 'referrals'} fulfilled
              · Next free month at <strong>{data.next_milestone_at}</strong>
            </p>
            <div className="w-full bg-white rounded-full h-3 overflow-hidden border border-slate-200" data-testid="referral-progress-bar">
              <div
                className="h-full bg-gradient-to-r from-[#b456df] to-[#56dfb4] transition-all duration-700"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">
              {data.progress_to_milestone} / 10 toward next free month
            </p>
          </CardContent>
        </Card>
      )}

      {/* Totals */}
      <Card className="border-slate-100" data-testid="referral-totals-card">
        <CardContent className="p-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-[#56dfb4]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="fulfilled-count">
                {data.fulfilled_referrals}
              </p>
              <p className="text-xs text-slate-500 mt-1">Upgraded · Reward credited</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-slate-400" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="pending-count">
                {data.pending_referrals}
              </p>
              <p className="text-xs text-slate-500 mt-1">Signed up · Waiting to upgrade</p>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mt-4 text-center">
            Rewards are credited the moment a friend upgrades to Verified Player. No limit.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
